import React, { useState } from 'react';
import classnames from 'classnames';
import { FaRegTrashAlt } from 'react-icons/fa';

// Coarse buckets rather than exact stamps: this list exists to help recognise a
// thread, and "2h ago" does that better than a formatted date does.
const formatWhen = (timestamp) => {
    const minutes = Math.round((Date.now() - timestamp) / 60000);
    if (minutes < 1) {
        return 'just now';
    }
    if (minutes < 60) {
        return `${minutes}m ago`;
    }

    const hours = Math.round(minutes / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }

    const days = Math.round(hours / 24);
    if (days === 1) {
        return 'yesterday';
    }
    if (days < 7) {
        return `${days} days ago`;
    }
    return new Date(timestamp).toLocaleDateString();
};

/**
 * The list of stored conversations, rendered identically in the widget's
 * slide-over and the full page's sidebar. Everything it shows comes from
 * localStorage - there is no server-side history to fall back on.
 */
const ChatHistory = ({ conversations, currentId, pendingConversationId, onSelect, onDelete, onClearAll }) => {
    const [confirmingClear, setConfirmingClear] = useState(false);

    if (!conversations.length) {
        return (
            <p className="chat-history-empty">
                Past conversations show up here once you have asked something.
            </p>
        );
    }

    return (
        <div className="chat-history">
            <ul className="chat-history-list">
                {conversations.map((conversation) => {
                    const isPending = conversation.id === pendingConversationId;

                    return (
                        <li
                            key={conversation.id}
                            className={classnames('chat-history-row', {
                                'chat-history-row-active': conversation.id === currentId
                            })}
                        >
                            <button
                                type="button"
                                className="chat-history-item"
                                onClick={() => onSelect(conversation.id)}
                            >
                                <span className="chat-history-title">{conversation.title}</span>
                                <span className="chat-history-meta">
                                    {isPending ? (
                                        <span className="chat-history-working">
                                            <span className="chat-history-working-dot" aria-hidden="true" />
                                            INNA is working…
                                        </span>
                                    ) : (
                                        <>
                                            {formatWhen(conversation.updatedAt)} ·{' '}
                                            {conversation.messages.length === 1
                                                ? '1 message'
                                                : `${conversation.messages.length} messages`}
                                        </>
                                    )}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="chat-history-delete"
                                onClick={() => onDelete(conversation.id)}
                                title="Delete conversation"
                                aria-label={`Delete conversation: ${conversation.title}`}
                            >
                                <FaRegTrashAlt />
                            </button>
                        </li>
                    );
                })}
            </ul>

            {/* Two steps rather than a modal: this throws away every transcript
                and cannot be undone, but it is not worth a dialog of its own. */}
            {confirmingClear ? (
                <div className="chat-history-confirm">
                    <span>Delete all {conversations.length} conversations?</span>
                    <div className="chat-history-confirm-actions">
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                                setConfirmingClear(false);
                                onClearAll();
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setConfirmingClear(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    className="chat-history-clear"
                    onClick={() => setConfirmingClear(true)}
                >
                    Clear all history
                </button>
            )}
        </div>
    );
};

export default ChatHistory;
